<template>
  <!-- Only show the child table when not collapsed -->
  <div v-show="!collapsed" class="child-activity-wrapper">
    <table class="table table-bordered table-striped table-hover child-activity-table">
      <thead class="table-light">
        <tr>
          <th
            v-for="(col, index) in mappingColumns.configs"
            :key="index"
            :style="{ textAlign: col.textAlign || 'center' }"
            class="align-middle"
          >
            <template v-if="col.name === 'checkbox'">
              <input type="checkbox" :checked="isAllSelected" @change="toggleSelectAll"          class="form-check-input" />
            </template>
            <template v-else>
              {{ col.label }}
            </template>
          </th>
        </tr>
      </thead>
      <tbody>
        <tr v-if="displayActivities.length === 0">
          <td :colspan="mappingColumns.configs.length" class="text-center fw-bold py-3">
            No activities available
          </td>
        </tr>
        <tr v-for="(activity, index) in displayActivities" :key="activity['Activity Id'] || index">
          <td
            v-for="(col, i) in mappingColumns.configs"
            :key="i"
            :style="{ textAlign: col.textAlign || 'center' }"
          >
            <template v-if="col.name === 'checkbox'">
              <input
                type="checkbox"
                :value="activity['Activity Id']"
                :checked="globalSelectedActivityIds.includes(activity['Activity Id'])"
                @change="toggleRowSelection($event, activity['Activity Id'])"
                         class="form-check-input"
              />
            </template>
            <template v-else>
              <div v-if="shouldTruncate(col.label, activity[col.name])">
                <span>
                  {{ truncateText(activity[col.name], 60) }}
                </span>
                <a href="#" @mouseover="showPopover($event, activity[col.name])" @mouseleave="hidePopover" style="font-size: inherit;">...</a>
              </div>
              <div v-else>
                {{ activity[col.name] }}
              </div>
            </template>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
  <transition name="fade">
<div
  v-if="globalSelectedActivityIds.length > 0"
  class="top-selection-bar"
>
  <div class="info">
    Activities Selected: <span>{{ globalSelectedActivityIds.length }}</span>
  </div>
  <button class="btn" @click="openViewPopup">View</button>
  <button class="btn1" @click="cancelSelection">Clear</button>
</div>

  </transition>
  <transition name="fade">
    <ViewChildPopup
      v-if="showViewPopup"
      :selectedActivities="selectedActivities"
      :childActivityViewData="childActivityViewData"
      :requestId="requestId"
      @close="closeViewPopup"
      @add-activities="handleAddActivities"
      @clear-selection="clearSelectedActivities"
    />
  </transition>
</template>

<script>
import { type, pageViews } from "../../utils/util";
import Store from "../../vuex/store.js";
import ViewChildPopup from "../ViewChildPopup.vue";
import axios from "axios"; // Add axios import if not already present
import { getCookie } from "../../store/cookie.js";
import { makeOpearationRequest } from "@/services/apiServices";
import { Popover } from 'bootstrap';


export default {
  name: pageViews.CHILD_ACTIVITIES_VIEW,
  components: { ViewChildPopup },
  props: {
    activities: { type: Array, default: () => [] },
    collapsed: { type: Boolean, default: false },
    requestId: { type: String, required: true }
  },
  data() {
    return {
      showViewPopup: false,
      mappingColumns: {
        type: type.CHILD_ACTIVITY, // Ensure this matches the backend expectation
        isMultiSelectionAllowed: true,
        defaultSortOrder: { name: "asc", label: "Ascending" },
        defaultSortColumn: { name: "Process", label: "Process" },
        configs: [
          { name: "checkbox", label: "Select", order: 0, isSortable: false, defaultSelected: false, textAlign: "center" },
          { name: "Activity", label: "Activity", order: 4, isSortable: true, defaultSelected: true, textAlign: "left" },
          { name: "Activity Type", label: "Activity Type", order: 5, isSortable: true, defaultSelected: true, textAlign: "left" },
          { name: "Is Starter", label: "Is Starter", order: 6, isSortable: true, defaultSelected: true, textAlign: "left" },
          { name: "Input", label: "Input", order: 8, isSortable: true, defaultSelected: true, textAlign: "left" },
          { name: "Output", label: "Output", order: 9, isSortable: true, defaultSelected: true, textAlign: "left" },
          { name: "Supporting Process Location", label: "Supporting Process Location", order: 10, isSortable: true, defaultSelected: true, textAlign: "left" },
          { name: "Supporting Process", label: "Supporting Process", order: 11, isSortable: true, defaultSelected: true, textAlign: "left" }
        ]
      },
      childActivityViewData: null // Store API response here
    };
  },
  computed: {
    displayActivities() {
      // Always return an array
      return Array.isArray(this.activities) ? this.activities : [];
    },
    globalSelectedActivityIds() {
      return Store.state.selectedActivityIds;
    },
    isAllSelected() {
      return (
        this.displayActivities.length > 0 &&
        this.displayActivities.every(act => this.globalSelectedActivityIds.includes(act["Activity Id"]))
      );
    },
    selectedActivities() {
      return this.displayActivities.filter(act => this.globalSelectedActivityIds.includes(act["Activity Id"]));
    }
  },
  watch: {
    activities(newVal) {
      console.log("activities prop changed:", newVal);
    }
  },
  methods: {
    toggleSelectAll(event) {
      if (event.target.checked) {
        this.displayActivities.forEach(act => {
          if (!this.globalSelectedActivityIds.includes(act["Activity Id"])) {
            Store.commit("addActivityId", act["Activity Id"]);
          }
        });
      } else {
        this.displayActivities.forEach(act => {
          if (this.globalSelectedActivityIds.includes(act["Activity Id"])) {
            Store.commit("removeActivityId", act["Activity Id"]);
          }
        });
      }
    },
    toggleRowSelection(event, activityId) {
      if (event.target.checked) {
        if (!this.globalSelectedActivityIds.includes(activityId)) {
          Store.commit("addActivityId", activityId);
        }
      } else {
        if (this.globalSelectedActivityIds.includes(activityId)) {
          Store.commit("removeActivityId", activityId);
        }
      }
    },
    cancelSelection() {
      Store.commit("clearSelectedActivities");
      this.closeViewPopup();
      this.$emit("collapse");
    },
    async fetchChildActivityView() {
      if (!this.globalSelectedActivityIds || this.globalSelectedActivityIds.length === 0) {
        console.warn("No activities selected for child activity view.");
        this.childActivityViewData = null;
        return;
      }
      const payload = {
        userId: getCookie("userId"),
        // activityIds: this.globalSelectedActivityIds,
        actvitiesIds: this.globalSelectedActivityIds,
        
        type: "MAPPER.CHILD_ACTIVITY_VIEW"
      };
      this.childActivityViewData = null;
      try {
        // Remove the undefined config argument
        console.log("Fetching child activity view with payload:", payload);
        const response = await makeOpearationRequest(this.$acclConfigs, "/childActivityView", payload);
        console.log("Child activity view response:", response.data);
        if (response.status === 200 && response.data) {
          this.childActivityViewData = response.data;
        }
      } catch (error) {
        console.error("Failed to fetch child activity view:", error);
        this.childActivityViewData = null;
      }
    },
    async openViewPopup() {
      await this.fetchChildActivityView();
      this.showViewPopup = true;
    },
    closeViewPopup() {
      this.showViewPopup = false;
    },
    handleAddActivities() {
      this.showViewPopup = false;
      // Optionally, clear any temporary data if needed
    },
    clearSelectedActivities() {
      Store.commit("clearSelectedActivities");
      this.$emit("collapse"); // Also close/collapse the child activities view
    },
    shouldTruncate(label, text) {
      const truncatableLabels = [
        'Usecase Location', 'Usecase', 'Process', 'Process Location', 'Sub Process', 'Sub Process Location',
        'Input Node', 'Binding Node', 'Condition', 'Condition Group', 'Input', 'Output',
        'Supporting Process Location', 'Supporting Process', 'Field Name', 'Reference Name',
        'Expression', 'Reference Field Name', 'Activity', 'Activity Type', 'Is Starter'
      ];
      return truncatableLabels.includes(label) && text && text.length > 60;
    },
    truncateText(text, length) {
      if (!text) return '';
      return text.length > length ? text.substring(0, length) : text;
    },
    showPopover(event, content) {
      const popover = new Popover(event.target, {
        content: content,
        trigger: 'hover',
        placement: 'auto',
        container: 'body',
        boundary: 'viewport',
        fallbackPlacements: ['top', 'bottom', 'left', 'right']
      });
      popover.show();
    },
    hidePopover(event) {
      const popover = Popover.getInstance(event.target);
      if (popover) {
        popover.hide();
      }
    },
  }
};
</script>

<style scoped>
/* Smooth reveal animation for the entire child section */
.child-activity-wrapper {
  animation: slideFadeIn 0.35s ease forwards;
  transform-origin: top;
  background: var(--primary);
  padding: 8px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.06);
}

/* Table styling */
.child-activity-table {
  border-radius: 6px;
  overflow: hidden;
  border: 1px solid var(--border-color);
}

.child-activity-table thead th {
  background: var(--primary);
  font-weight: 600;
  padding: 12px;
  border-bottom: 1px solid var(--border-color);
}

.child-activity-table td {
  padding: 10px 12px;
  vertical-align: middle;
  background: var(--surface);
  color: var(--text-primary);
  transition: background 0.2s ease;
}

/* Hover effect per row */
.child-activity-table tbody tr:hover td {
  background: var(--surface-hover);
}

/* Checkbox alignment fix */
.child-activity-table input[type="checkbox"] {
  width: 16px;
  height: 16px;
}

/* Bottom bar animation */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.28s ease, transform 0.28s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: translateY(10px);
}

/* Selected Activities banner */
body .selected-banner {
  background: var(--primary);
  color: #fff;
  border-radius: 8px;
  box-shadow: 0 6px 14px rgba(0,0,0,0.15);
}

.form-check-input {
  background-color: var(--surface);
  border: 1px solid var(--text-secondary);
  cursor: pointer;
}

.form-check-input:checked {
  background-color: var(--primary);
  border: 1px solid var(--text-secondary);
  border-color: var(--primary);
}

/* Popup fade */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Opening animation keyframes */
@keyframes slideFadeIn {
  0% {
    opacity: 0;
    transform: translateY(-8px) scaleY(0.97);
  }
  100% {
    opacity: 1;
    transform: translateY(0) scaleY(1);
  }
}

/* Subtle hover on action buttons */
button.btn {
  transition: background 0.2s ease, transform 0.2s ease;
}

button.btn:hover {
  transform: translateY(-2px);
  background: var(--primary-light);
}

/* Fade animation you already use */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.28s ease, transform 0.28s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

/* Floating top bar (your selected counter) */
.top-selection-bar {
  position: fixed;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  padding: 10px 18px;
  background: var(--surface);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.12);
  display: flex;
  align-items: center;
  gap: 10px;
  z-index: 1040;
  animation: dropDown 0.35s ease forwards;
}

/* Text block */
.top-selection-bar .info {
  font-weight: 600;
  font-size: 0.95rem;
}

/* Buttons */
.top-selection-bar .btn {
  padding: 6px 14px;
  border-radius: 6px;
  font-size: 0.9rem;
  background: var(--primary);
  color: #fff;
  transition: transform 0.2s ease, background 0.2s ease;
}

.top-selection-bar .btn1 {
  padding: 6px 14px;
  border-radius: 6px;
  font-size: 0.9rem;
  background: var(--primary);
  margin: 0;
  color: #fff;
  transition: transform 0.2s ease, background 0.2s ease;
}

.top-selection-bar .btn:hover {
  transform: translateY(-2px);
  background: var(--primary);
}

/* Clear button special look */
.top-selection-bar .btn:nth-child(3) {
  background: var(--danger);
}
.top-selection-bar .btn:nth-child(3):hover {
  background: var(--danger-light);
}

/* Entry animation */
@keyframes dropDown {
  0% {
    opacity: 0;
    transform: translate(-50%, -12px);
  }
  100% {
    opacity: 1;
    transform: translate(-50%, 0);
  }
}


</style>